// "الشهادات" — the certificates for each edition's students, worked like the
// technical sheets page: sales upload the list of students who finished the
// run (one spreadsheet, names as they must be printed) and so ask the designer
// for the certificates; the designer uploads them — one file, several, or a
// zip — or shares a folder link holding them, and delivers; sales can send
// them back for changes. One request per edition.
//
// Who may write which half is in src/lib/roles.ts, the status moves in
// src/lib/degrees.ts; both are enforced again by `degrees.*` and the upload
// route — the hidden buttons here are convenience.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	CalendarClock,
	Check,
	Download,
	ExternalLink,
	FileCheck2,
	FileSpreadsheet,
	FolderOpen,
	Loader2,
	Trash2,
	Upload,
	UserCheck,
	Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { GhostButton, Modal, PrimaryButton } from "#/components/admin/modal";
import { TabBar, type TabItem } from "#/components/admin/tabs";
import { authClient } from "#/lib/auth-client";
import {
	DEGREE_ACCEPT_ATTR,
	DEGREE_STATUS_LABELS,
	type DegreeFileKind,
	type DegreeStatus,
	degreeActions,
	degreeWaitsOn,
	validateDegreeUpload,
} from "#/lib/degrees";
import { editionLabel, isoDay } from "#/lib/editions";
import { isPreviewable } from "#/lib/fiches";
import { algiersToday } from "#/lib/lead-filters";
import {
	canEditDegreeFiles,
	canEditDegreeList,
	canViewDegrees,
	homeRouteFor,
	isDesigner,
} from "#/lib/roles";
import { getSession } from "#/lib/session";
import { formatBytes } from "#/lib/uploads";
import { dayLabel } from "#/lib/weekly-report";

const FILTERS = [
	"mine",
	"all",
	"REQUESTED",
	"IN_PROGRESS",
	"DELIVERED",
	"none",
] as const;
type Filter = (typeof FILTERS)[number];

const searchSchema = z.object({
	filter: z.enum(FILTERS).optional(),
	// The open edition — a link sales can send the designer straight to.
	edition: z.coerce.number().int().optional(),
});

type DegreesSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/degrees")({
	validateSearch: (search: Record<string, unknown>): DegreesSearch => {
		const parsed = searchSchema.safeParse(search);
		return parsed.success ? parsed.data : {};
	},
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (!canViewDegrees(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: DegreesPage,
});

const CARD = "rounded-2xl border border-line bg-white shadow-card";
const ICON_BUTTON =
	"rounded-full p-2 text-muted transition-colors hover:bg-canvas hover:text-navy";
const INPUT =
	"w-full rounded-xl border border-line bg-white px-3 py-2 text-sm font-normal text-navy outline-none focus:border-teal";

const STATUS_STYLES: Record<DegreeStatus | "none", string> = {
	REQUESTED: "bg-amber-50 text-amber-800",
	IN_PROGRESS: "bg-teal-50 text-teal-600",
	DELIVERED: "bg-emerald-50 text-emerald-700",
	none: "bg-canvas text-muted",
};

function StatusBadge({ status }: { status: DegreeStatus | null }) {
	return (
		<span
			className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[status ?? "none"]}`}
		>
			{status ? DEGREE_STATUS_LABELS[status] : "لا توجد قائمة"}
		</span>
	);
}

function formatDateTime(value: string | Date) {
	return new Intl.DateTimeFormat("ar-DZ", {
		dateStyle: "medium",
		timeStyle: "short",
		timeZone: "Africa/Algiers",
	}).format(new Date(value));
}

function runLabel(start: string | Date, end: string | Date) {
	const from = isoDay(start);
	const to = isoDay(end);
	return from === to ? dayLabel(from) : `${dayLabel(from)} ← ${dayLabel(to)}`;
}

function DegreesPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	const { data: session } = authClient.useSession();
	const role = session?.user?.role;
	const navigate = useNavigate({ from: Route.fullPath });
	const search = Route.useSearch();
	// The designer opens on their queue; sales and admins on every run, since
	// a run with no list yet is theirs to start.
	const defaultFilter: Filter = isDesigner(role) ? "mine" : "all";
	const filter: Filter = search.filter ?? defaultFilter;
	const today = algiersToday();

	const query = useQuery(trpc.degrees.list.queryOptions());
	const rows = query.data ?? [];

	const matches: Record<Filter, (row: (typeof rows)[number]) => boolean> = {
		mine: (row) =>
			row.degree ? degreeWaitsOn(role, row.degree.status) : false,
		all: () => true,
		REQUESTED: (row) => row.degree?.status === "REQUESTED",
		IN_PROGRESS: (row) => row.degree?.status === "IN_PROGRESS",
		DELIVERED: (row) => row.degree?.status === "DELIVERED",
		none: (row) => !row.degree,
	};

	const tabs: TabItem<Filter>[] = [
		{ key: "mine", label: "بانتظارك", count: rows.filter(matches.mine).length },
		{ key: "all", label: "الكل", count: rows.length },
		{
			key: "REQUESTED",
			label: DEGREE_STATUS_LABELS.REQUESTED,
			count: rows.filter(matches.REQUESTED).length,
		},
		{
			key: "IN_PROGRESS",
			label: DEGREE_STATUS_LABELS.IN_PROGRESS,
			count: rows.filter(matches.IN_PROGRESS).length,
		},
		{
			key: "DELIVERED",
			label: DEGREE_STATUS_LABELS.DELIVERED,
			count: rows.filter(matches.DELIVERED).length,
		},
		{
			key: "none",
			label: "بدون قائمة",
			count: rows.filter(matches.none).length,
		},
	];

	function setSearch(next: DegreesSearch) {
		void navigate({ search: next, replace: true });
	}

	const intro = canEditDegreeFiles(role)
		? canEditDegreeList(role)
			? "قائمة المتخرّجين في كل طبعة والشهادات المنجزة لهم."
			: "أنجز شهادات المتخرّجين في كل طبعة: ارفع الشهادات (ملفات أو ملف ZIP)، أو شارك رابط مجلد يضمّها كلها."
		: "ارفع قائمة المتخرّجين في كل طبعة (ملف Excel مثلاً) كما يجب أن تُكتب أسماؤهم، ليُنجز المصمّم شهاداتهم.";

	// The list comes most recent run first; this filter keeps that order.
	const visible = rows.filter(matches[filter]);

	return (
		<div className="flex flex-col gap-5">
			<div>
				<h1 className="text-2xl font-bold text-navy">الشهادات</h1>
				<p className="text-sm text-muted">{intro}</p>
			</div>

			<TabBar
				tabs={tabs}
				value={filter}
				onChange={(key) =>
					setSearch({
						...search,
						filter: key === defaultFilter ? undefined : key,
					})
				}
			/>

			{query.isLoading ? (
				<div className={`flex justify-center py-16 ${CARD}`}>
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : query.isError ? (
				<p className={`px-6 py-10 text-center text-sm text-red-600 ${CARD}`}>
					{query.error.message}
				</p>
			) : visible.length === 0 ? (
				<p className={`px-6 py-10 text-center text-sm text-muted ${CARD}`}>
					{filter === "mine"
						? "لا شيء بانتظارك الآن."
						: "لا توجد طبعات في هذه القائمة."}
				</p>
			) : (
				<ul className={`divide-y divide-line ${CARD}`}>
					{visible.map((row) => {
						const due = row.degree?.dueDate ? isoDay(row.degree.dueDate) : null;
						const late =
							due !== null && due < today && row.degree?.status !== "DELIVERED";
						return (
							<li key={row.courseNumber}>
								<button
									type="button"
									onClick={() =>
										setSearch({ ...search, edition: row.courseNumber })
									}
									className="flex w-full flex-col gap-2 px-5 py-4 text-start transition-colors hover:bg-canvas/60 sm:flex-row sm:items-center sm:gap-4"
								>
									<div className="flex min-w-0 flex-1 flex-col gap-1">
										<div className="flex flex-wrap items-center gap-2">
											<span className="font-bold text-navy">
												{row.training.name}
											</span>
											<span className="text-sm font-bold text-muted">
												{editionLabel(row.editionNumber)}
											</span>
											<StatusBadge status={row.degree?.status ?? null} />
										</div>
										<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
											<span>{runLabel(row.startDate, row.endDate)}</span>
											{row.degree ? (
												<>
													<span className="inline-flex items-center gap-1">
														<Users className="h-3.5 w-3.5" />
														{row.degree.lists ? "القائمة مرفوعة" : "بدون قائمة"}
													</span>
													{row.degree.certificates ? (
														<span className="inline-flex items-center gap-1">
															<FileCheck2 className="h-3.5 w-3.5" />
															{row.degree.certificates === 1
																? "ملف شهادات"
																: `${row.degree.certificates} ملفات شهادات`}
														</span>
													) : null}
													{row.degree.hasDriveUrl ? (
														<span className="inline-flex items-center gap-1">
															<FolderOpen className="h-3.5 w-3.5" />
															مجلد مشترك
														</span>
													) : null}
												</>
											) : null}
											{row.confirmed !== null && row.confirmed > 0 ? (
												<span className="inline-flex items-center gap-1">
													<UserCheck className="h-3.5 w-3.5" />
													{row.confirmed} طلب مؤكّد
												</span>
											) : null}
										</div>
									</div>
									{due ? (
										<span
											className={`inline-flex shrink-0 items-center gap-1.5 text-xs font-bold ${late ? "text-red-600" : "text-navy"}`}
										>
											<CalendarClock className="h-4 w-4" />
											{late ? "متأخر — " : "مطلوب قبل "}
											{dayLabel(due)}
										</span>
									) : null}
								</button>
							</li>
						);
					})}
				</ul>
			)}

			{search.edition !== undefined ? (
				<DegreeDialog
					editionId={search.edition}
					role={role}
					onClose={() => setSearch({ ...search, edition: undefined })}
				/>
			) : null}
		</div>
	);
}

function DegreeDialog({
	editionId,
	role,
	onClose,
}: {
	editionId: number;
	role: string | null | undefined;
	onClose: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const queryClient = useQueryClient();
	const query = useQuery(trpc.degrees.get.queryOptions({ editionId }));
	const [error, setError] = useState<string | null>(null);

	function refresh() {
		void queryClient.invalidateQueries({
			queryKey: trpc.degrees.get.queryKey({ editionId }),
		});
		void queryClient.invalidateQueries({
			queryKey: trpc.degrees.list.queryKey(),
		});
	}

	const statusMutation = useMutation({
		...trpc.degrees.setStatus.mutationOptions(),
		onSuccess: refresh,
		onError: (err) => setError(err.message),
	});

	const edition = query.data;
	const degree = edition?.degree ?? null;
	const lists = degree?.files.filter((f) => f.kind === "LIST") ?? [];
	const certificates =
		degree?.files.filter((f) => f.kind === "CERTIFICATES") ?? [];
	const actions = degree
		? degreeActions(role, degree.status, {
				lists: lists.length,
				certificates: certificates.length,
				hasDriveUrl: Boolean(degree.driveUrl),
			})
		: [];
	const editsList = canEditDegreeList(role);
	const editsFiles = canEditDegreeFiles(role);

	return (
		<Modal
			title={
				edition
					? `${edition.training.name} — ${editionLabel(edition.editionNumber)}`
					: "الشهادات"
			}
			subtitle={
				edition ? (
					<span className="flex flex-wrap items-center gap-2">
						<StatusBadge status={degree?.status ?? null} />
						<span>{runLabel(edition.startDate, edition.endDate)}</span>
						{degree?.requestedBy ? (
							<span>· طلبها {degree.requestedBy.name}</span>
						) : null}
						{degree?.deliveredAt ? (
							<span>· سُلّمت {formatDateTime(degree.deliveredAt)}</span>
						) : null}
					</span>
				) : null
			}
			width="xl"
			onClose={onClose}
			footer={
				<div className="flex flex-wrap items-center justify-between gap-2">
					{error ? (
						<p className="text-sm font-semibold text-red-600">{error}</p>
					) : (
						<span />
					)}
					<div className="flex flex-wrap gap-2">
						<GhostButton onClick={onClose}>إغلاق</GhostButton>
						{actions.map((action) => (
							<PrimaryButton
								key={action.to}
								disabled={statusMutation.isPending}
								onClick={() => {
									setError(null);
									statusMutation.mutate({ editionId, status: action.to });
								}}
							>
								{statusMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Check className="h-4 w-4" />
								)}
								{action.label}
							</PrimaryButton>
						))}
					</div>
				</div>
			}
		>
			{query.isLoading ? (
				<div className="flex justify-center py-10">
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : query.isError || !edition ? (
				<p className="py-10 text-center text-sm text-red-600">
					{query.error?.message ?? "الطبعة غير موجودة"}
				</p>
			) : (
				<div className="grid gap-6 lg:grid-cols-2">
					<section className="flex flex-col gap-4">
						<h3 className="flex items-center gap-2 font-bold text-navy">
							<Users className="h-4.5 w-4.5" />
							قائمة المتخرّجين
						</h3>
						<FileList
							files={lists}
							canEdit={editsList}
							onChanged={refresh}
							empty="لم تُرفع قائمة المتخرّجين بعد."
						/>
						{editsList ? (
							<div className="flex flex-col gap-2">
								<UploadButton
									editionId={editionId}
									kind="LIST"
									label={
										lists.length ? "رفع نسخة جديدة" : "رفع قائمة المتخرّجين"
									}
									onUploaded={refresh}
								/>
								{/* A spreadsheet in the designer's columns, pre-filled with
								    the run's confirmed orders — names only. */}
								<a
									href={`/api/admin/degree-list-template/${editionId}`}
									className="inline-flex items-center gap-1.5 self-start text-xs font-bold text-teal-600 hover:underline"
								>
									<FileSpreadsheet className="h-4 w-4" />
									تنزيل نموذج القائمة (Excel)
									{edition.confirmed ? (
										<span className="font-normal text-muted">
											— بأسماء {edition.confirmed} طلب مؤكّد
										</span>
									) : null}
								</a>
							</div>
						) : null}
						<BriefEditor
							editionId={editionId}
							brief={degree?.brief ?? null}
							dueDate={degree?.dueDate ? isoDay(degree.dueDate) : null}
							canEdit={editsList}
							onSaved={refresh}
						/>
					</section>

					<section className="flex flex-col gap-4 lg:border-s lg:border-line lg:ps-6">
						<h3 className="flex items-center gap-2 font-bold text-navy">
							<FileCheck2 className="h-4.5 w-4.5" />
							الشهادات
						</h3>
						{degree ? (
							<>
								<FileList
									files={certificates}
									canEdit={editsFiles}
									onChanged={refresh}
									empty="لم تُرفع الشهادات بعد."
								/>
								{editsFiles ? (
									<UploadButton
										editionId={editionId}
										kind="CERTIFICATES"
										label="رفع الشهادات"
										onUploaded={refresh}
									/>
								) : null}
								<DeliveryEditor
									editionId={editionId}
									driveUrl={degree.driveUrl}
									note={degree.designNote}
									canEdit={editsFiles}
									onSaved={refresh}
								/>
							</>
						) : (
							<p className="rounded-xl bg-canvas px-4 py-6 text-center text-sm text-muted">
								تظهر الشهادات هنا بعد أن يرفع فريق المبيعات قائمة المتخرّجين.
							</p>
						)}
					</section>
				</div>
			)}
		</Modal>
	);
}

/* -------------------------------------------------------------------------- */
/*  Files                                                                     */
/* -------------------------------------------------------------------------- */

interface DegreeFileRow {
	id: string;
	kind: DegreeFileKind;
	name: string;
	contentType: string;
	size: number;
	createdAt: string | Date;
	uploadedBy: { name: string } | null;
}

function fileUrl(id: string, download = false) {
	return `/api/admin/degree-file/${id}${download ? "?download=1" : ""}`;
}

function FileList({
	files,
	canEdit,
	onChanged,
	empty,
}: {
	files: DegreeFileRow[];
	canEdit: boolean;
	onChanged: () => void;
	empty: string;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [confirming, setConfirming] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const removeMutation = useMutation({
		...trpc.degrees.removeFile.mutationOptions(),
		onSuccess: () => {
			setConfirming(null);
			onChanged();
		},
		onError: (err) => setError(err.message),
	});

	if (!files.length) {
		return (
			<p className="rounded-xl bg-canvas px-4 py-6 text-center text-sm text-muted">
				{empty}
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			<ul className="flex flex-col gap-2">
				{files.map((file) => (
					<li
						key={file.id}
						className="flex items-center gap-3 rounded-xl border border-line p-3"
					>
						<div className="min-w-0 flex-1">
							<p className="truncate text-sm font-bold text-navy" dir="auto">
								{file.name}
							</p>
							{/* <bdi> per part: "329 KB" and the date are LTR runs that the
							    RTL paragraph would otherwise splice together. */}
							<p className="text-xs text-muted">
								<bdi>{formatBytes(file.size)}</bdi> ·{" "}
								<bdi>{formatDateTime(file.createdAt)}</bdi>
								{file.uploadedBy ? (
									<>
										{" "}
										· <bdi>{file.uploadedBy.name}</bdi>
									</>
								) : null}
							</p>
						</div>
						{isPreviewable(file.contentType) ? (
							<a
								href={fileUrl(file.id)}
								target="_blank"
								rel="noreferrer"
								className={ICON_BUTTON}
								aria-label="فتح"
								title="فتح"
							>
								<ExternalLink className="h-4 w-4" />
							</a>
						) : null}
						<a
							href={fileUrl(file.id, true)}
							className={ICON_BUTTON}
							aria-label="تحميل"
							title="تحميل"
						>
							<Download className="h-4 w-4" />
						</a>
						{canEdit ? (
							confirming === file.id ? (
								<span className="flex items-center gap-1">
									<button
										type="button"
										disabled={removeMutation.isPending}
										onClick={() => {
											setError(null);
											removeMutation.mutate({ fileId: file.id });
										}}
										className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
									>
										{removeMutation.isPending ? "…" : "تأكيد الحذف"}
									</button>
									<button
										type="button"
										onClick={() => setConfirming(null)}
										className="rounded-full px-2 py-1.5 text-xs font-bold text-muted hover:text-navy"
									>
										إلغاء
									</button>
								</span>
							) : (
								<button
									type="button"
									onClick={() => setConfirming(file.id)}
									className="rounded-full p-2 text-muted transition-colors hover:bg-red-50 hover:text-red-600"
									aria-label="حذف"
									title="حذف"
								>
									<Trash2 className="h-4 w-4" />
								</button>
							)
						) : null}
					</li>
				))}
			</ul>
			{error ? (
				<p className="text-xs font-semibold text-red-600">{error}</p>
			) : null}
		</div>
	);
}

function UploadButton({
	editionId,
	kind,
	label,
	onUploaded,
}: {
	editionId: number;
	kind: DegreeFileKind;
	label: string;
	onUploaded: () => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function upload(files: File[]) {
		setError(null);
		// Check them all first, so one bad file doesn't leave half a batch.
		for (const file of files) {
			const invalid = validateDegreeUpload(kind, file);
			if (invalid) {
				setError(`${file.name}: ${invalid}`);
				return;
			}
		}
		try {
			for (const file of files) {
				setBusy(file.name);
				const body = new FormData();
				body.append("file", file);
				body.append("kind", kind);
				body.append("editionId", String(editionId));
				const response = await fetch("/api/admin/degree-upload", {
					method: "POST",
					body,
				});
				const payload = (await response.json().catch(() => null)) as {
					error?: string;
				} | null;
				if (!response.ok) {
					setError(payload?.error ?? "تعذّر رفع الملف. حاول مجدداً.");
					return;
				}
				onUploaded();
			}
		} catch {
			setError("تعذّر الاتصال بالخادم. حاول مجدداً.");
		} finally {
			setBusy(null);
			if (inputRef.current) inputRef.current.value = "";
		}
	}

	return (
		<div className="flex flex-col gap-1.5">
			<input
				ref={inputRef}
				type="file"
				// The certificates may come as several files; the list is one.
				multiple={kind === "CERTIFICATES"}
				accept={DEGREE_ACCEPT_ATTR[kind]}
				className="hidden"
				onChange={(e) => {
					const files = Array.from(e.target.files ?? []);
					if (files.length) void upload(files);
				}}
			/>
			<GhostButton
				disabled={busy !== null}
				onClick={() => inputRef.current?.click()}
				className="self-start"
			>
				{busy ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Upload className="h-4 w-4" />
				)}
				{busy ? `جارٍ رفع ${busy}…` : label}
			</GhostButton>
			<p className="text-xs text-muted">
				{kind === "LIST"
					? "ملف Excel أو CSV أو PDF أو Word أو صورة — حتى 25 MB."
					: "ملفات PDF أو صور، أو ملف ZIP يضمّها كلها (حتى 150 MB)."}
			</p>
			{error ? (
				<p className="text-xs font-semibold text-red-600">{error}</p>
			) : null}
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Brief (sales) and delivery (designer)                                     */
/* -------------------------------------------------------------------------- */

function BriefEditor({
	editionId,
	brief,
	dueDate,
	canEdit,
	onSaved,
}: {
	editionId: number;
	brief: string | null;
	dueDate: string | null;
	canEdit: boolean;
	onSaved: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [text, setText] = useState(brief ?? "");
	const [due, setDue] = useState(dueDate ?? "");
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Pick up what the server holds whenever it changes under us.
	useEffect(() => {
		setText(brief ?? "");
		setDue(dueDate ?? "");
	}, [brief, dueDate]);

	const mutation = useMutation({
		...trpc.degrees.saveBrief.mutationOptions(),
		onSuccess: () => {
			setSaved(true);
			onSaved();
		},
		onError: (err) => setError(err.message),
	});

	if (!canEdit) {
		return (
			<div className="flex flex-col gap-2 rounded-xl bg-canvas p-4">
				<p className="text-xs font-bold text-muted">توجيهات للمصمّم</p>
				<p className="whitespace-pre-line text-sm text-navy" dir="auto">
					{brief || "لا توجد توجيهات."}
				</p>
				{dueDate ? (
					<p className="inline-flex items-center gap-1.5 text-xs font-bold text-navy">
						<CalendarClock className="h-4 w-4" />
						مطلوب قبل {dayLabel(dueDate)}
					</p>
				) : null}
			</div>
		);
	}

	const dirty = text !== (brief ?? "") || due !== (dueDate ?? "");

	return (
		<div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				توجيهات للمصمّم
				<textarea
					value={text}
					onChange={(e) => {
						setText(e.target.value);
						setSaved(false);
					}}
					rows={3}
					maxLength={4000}
					dir="auto"
					placeholder="النموذج المعتمد، اللغة، عدد الساعات المذكور، أي ملاحظة."
					className={INPUT}
				/>
			</label>
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				مطلوب قبل
				<input
					type="date"
					value={due}
					onChange={(e) => {
						setDue(e.target.value);
						setSaved(false);
					}}
					className={`${INPUT} sm:w-48`}
				/>
			</label>
			<div className="flex items-center gap-3">
				<GhostButton
					disabled={!dirty || mutation.isPending}
					onClick={() => {
						setError(null);
						mutation.mutate({
							editionId,
							brief: text.trim() || null,
							dueDate: due || null,
						});
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : null}
					حفظ التوجيهات
				</GhostButton>
				{saved && !dirty ? (
					<span className="text-xs font-bold text-emerald-700">تم الحفظ</span>
				) : null}
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}

function DeliveryEditor({
	editionId,
	driveUrl,
	note,
	canEdit,
	onSaved,
}: {
	editionId: number;
	driveUrl: string | null;
	note: string | null;
	canEdit: boolean;
	onSaved: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [url, setUrl] = useState(driveUrl ?? "");
	const [text, setText] = useState(note ?? "");
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setUrl(driveUrl ?? "");
		setText(note ?? "");
	}, [driveUrl, note]);

	const mutation = useMutation({
		...trpc.degrees.saveDelivery.mutationOptions(),
		onSuccess: () => {
			setSaved(true);
			onSaved();
		},
		onError: (err) => setError(err.message),
	});

	const folderLink = driveUrl ? (
		<a
			href={driveUrl}
			target="_blank"
			rel="noreferrer noopener"
			className="inline-flex items-center gap-1.5 self-start rounded-full bg-teal-50 px-3 py-1.5 text-sm font-bold text-teal-600 hover:bg-teal-100"
		>
			<FolderOpen className="h-4 w-4" />
			فتح مجلد الشهادات
		</a>
	) : null;

	if (!canEdit) {
		return (
			<div className="flex flex-col gap-2 rounded-xl bg-canvas p-4">
				<p className="text-xs font-bold text-muted">التسليم</p>
				{folderLink ?? (
					<p className="text-sm text-muted">لا يوجد مجلد مشترك.</p>
				)}
				{note ? (
					<p className="whitespace-pre-line text-sm text-navy" dir="auto">
						{note}
					</p>
				) : null}
			</div>
		);
	}

	const dirty = url !== (driveUrl ?? "") || text !== (note ?? "");

	return (
		<div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				رابط مجلد يضمّ كل الشهادات (Google Drive…)
				<input
					type="url"
					value={url}
					onChange={(e) => {
						setUrl(e.target.value);
						setSaved(false);
					}}
					dir="ltr"
					placeholder="https://drive.google.com/drive/folders/…"
					className={INPUT}
				/>
				<span className="font-normal">
					بديل عن رفع الشهادات. تأكّد أن الرابط مفتوح لفريق المبيعات.
				</span>
			</label>
			{folderLink}
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				ملاحظة لفريق المبيعات
				<textarea
					value={text}
					onChange={(e) => {
						setText(e.target.value);
						setSaved(false);
					}}
					rows={2}
					maxLength={4000}
					dir="auto"
					className={INPUT}
				/>
			</label>
			<div className="flex items-center gap-3">
				<GhostButton
					disabled={!dirty || mutation.isPending}
					onClick={() => {
						setError(null);
						mutation.mutate({
							editionId,
							driveUrl: url.trim() || null,
							designNote: text.trim() || null,
						});
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : null}
					حفظ
				</GhostButton>
				{saved && !dirty ? (
					<span className="text-xs font-bold text-emerald-700">تم الحفظ</span>
				) : null}
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}
